import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { unlockBeep } from "../lib/beep";
import { APP_VERSION } from "../version";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import ThemeToggle from "./ThemeToggle";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("qre_email");
    if (saved) setEmail(saved);

    const token = localStorage.getItem("qre_token");
    if (!token) { setChecking(false); return; }

    fetch(`${API}/auth/me`, {
      headers: { "Authorization": `Bearer ${token}` }
    }).then(r => {
      if (r.ok) navigate("/scanner");
      else setChecking(false);
    }).catch(() => setChecking(false));
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim().toLowerCase();

    if (!trimmed) { setError("Ingresa un correo electrónico"); return; }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
      setError("Formato de correo inválido"); return;
    }

    setLoading(true);
    unlockBeep();
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.needs_password) {
          setNeedsPassword(true);
          setError("Ingresa tu contraseña");
        } else if (data.session_token) {
          localStorage.setItem("qre_email", trimmed);
          localStorage.setItem("qre_token", data.session_token);
          if (data.is_admin) localStorage.setItem("qre_admin", "1");
          else localStorage.removeItem("qre_admin");
          navigate("/scanner", { replace: true });
        }
      } else {
        const data = await response.json();
        setError(data.detail || "Error al iniciar sesión");
      }
    } catch (err) {
      setError("Error de conexión. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center gap-4">
        <div className="w-12 h-12 rounded-full border-4 border-secondary border-t-primary animate-spin"></div>
        <div className="text-muted-foreground text-base">Verificando sesión...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-5 bg-gradient-to-br from-background to-secondary relative">
      <ThemeToggle className="absolute top-4 right-4" />
      <div className="bg-card rounded-2xl shadow-[0_8px_32px_rgba(12,68,124,0.15)] text-center max-w-[400px] w-full p-10">
        <img src="/logo.svg" alt="QRE" className="w-16 h-16 rounded-2xl mb-4 mx-auto" />
        <h1 className="font-brand text-3xl font-bold text-primary mb-3" data-testid="login-title">Escáner QR</h1>
        <p className="text-base text-muted-foreground mb-8">Ingresa tu correo para comenzar a escanear</p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            type="email"
            className="h-12 text-base border-2 focus-visible:border-primary"
            placeholder="tu@correo.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setNeedsPassword(false); setPassword(""); }}
            autoComplete="email"
            autoFocus
            data-testid="email-input"
          />
          {needsPassword && (
            <Input
              type="password"
              className="h-12 text-base border-2 focus-visible:border-primary"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              data-testid="password-input"
            />
          )}
          {error && <div className="text-destructive text-sm text-left" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full h-12 text-base" disabled={loading} data-testid="login-button">
            {loading ? "Ingresando..." : "Entrar"}
          </Button>
        </form>
        <div className="mt-6 text-[11px] text-muted-foreground text-center" data-testid="app-version">
          v{APP_VERSION}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
